import { useState } from 'react';
import { Globe } from 'lucide-react';
import Logo from '@/assets/logo.svg?react';

const LOGIN_BG_VIDEO = '/pub_assets/videos/login_bg.mp4';
const LOGIN_BG_IMAGE = '/pub_assets/images/login_mbg.png';

export type BrandBackgroundMode = 'image' | 'video' | 'auto';

export interface BrandSectionProps {
  /** 背景展示模式：image 仅图片，video 仅视频，auto 图片占位、视频加载完成后替换 */
  backgroundMode?: BrandBackgroundMode;
}

/**
 * 品牌区域左侧组件
 * 包含 Logo、标语、背景图/视频、描述文字和网站链接
 * 视频加载完成后用视频替换背景图（auto 模式）
 */
export function BrandSection({ backgroundMode = 'auto' }: BrandSectionProps) {
  const [videoLoaded, setVideoLoaded] = useState(false);

  const showImage = backgroundMode === 'image' || (backgroundMode === 'auto' && !videoLoaded);
  const showVideo = backgroundMode === 'video' || (backgroundMode === 'auto' && videoLoaded);

  return (
    <div className="relative h-[666px] w-[500px] overflow-hidden bg-[#002021]">
      {/* 背景区域：图片作为占位，视频加载完成后替换 */}
      <div className="absolute inset-0 opacity-70">
        {/* 背景图 */}
        {backgroundMode !== 'video' && (
          <div
            className="h-full w-full bg-cover bg-center transition-opacity duration-500"
            style={{
              backgroundImage: `url(${LOGIN_BG_IMAGE})`,
              opacity: showImage ? 1 : 0,
            }}
          />
        )}
        {/* 背景视频 */}
        {backgroundMode !== 'image' && (
          <video
            src={LOGIN_BG_VIDEO}
            className="absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-500"
            style={{ opacity: showVideo ? 1 : 0 }}
            muted
            autoPlay
            loop
            playsInline
            onCanPlayThrough={() => setVideoLoaded(true)}
          />
        )}
      </div>

      {/* Logo */}
      <div className="absolute left-[40px] top-[36px] h-[24px] w-[155px]">
        <Logo className="h-full w-auto " />
      </div>

      {/* 主标语 */}
      <div className="absolute left-[40px] top-[112px] w-[420px]">
        <h1
          className="font-bold leading-[64px] tracking-[-1.04px] text-white opacity-[0.98]"
          style={{
            fontSize: '52px',
            fontFamily: 'Inter, sans-serif',
            textShadow: '0px 9px 14px rgba(25, 70, 159, 0.25)',
          }}
        >
          We Source Suppliers
          <br />
          Worth Your
          <br />
          {/* <span className="bg-gradient-to-r from-[#10b981] to-[#34d399] bg-clip-text text-transparent"> */}
          Time
          {/* </span> */}
        </h1>
      </div>

      {/* 分隔线 */}
      <div className="absolute left-[40px] top-[520px] h-[6px] w-[32px] bg-white" />

      {/* 描述文字 */}
      <p className="absolute left-[41px] top-[536px] w-[394px] text-[14px] font-normal leading-[1.5] tracking-[0.07px] text-white">
        We handle first-round supplier screening so you can decide in days, not weeks.
      </p>

      {/* 网站链接 */}
      <div className="absolute left-[40px] top-[613px] flex items-center gap-[8px]">
        <Globe className="h-[16px] w-[16px] text-white" />
        <span className="text-[14px] font-normal leading-[1.5] tracking-[0.07px] text-white">easesourcing.com</span>
      </div>
    </div>
  );
}

export default BrandSection;
